/**
 * Registre des contenus d'aide contextuelle.
 *
 * Chaque contexte (clé) peut avoir deux variantes de documentation :
 *  - `user` : aide destinée aux utilisateurs finaux (visible par tous) ;
 *  - `dev`  : aide technique, réservée aux USER_MASTER.
 *
 * Convention de nommage des fichiers dans `boost-crm/docs/help/` :
 *  - `<clé>.user.md` → variante utilisateur ;
 *  - `<clé>.dev.md`  → variante développeur ;
 *  - `<clé>.md`      → traité comme variante utilisateur (compatibilité).
 *
 * Les fichiers sont inlinés au build via `import.meta.glob` (eager + ?raw).
 * NB : le dossier docs est hors de la racine Vite ; l'ajout d'un *nouveau*
 * fichier nécessite un redémarrage du serveur de dev.
 */

export type HelpVariant = 'user' | 'dev'

// Glob relatif à ce fichier : front/src/help -> front/src -> front -> boost-crm
const modules = import.meta.glob('../../../docs/help/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

/** Map `clé -> { user?, dev? }`. */
const helpByKey: Record<string, Partial<Record<HelpVariant, string>>> = {}

for (const path in modules) {
  // ex. '../../../docs/help/partner.dev.md' -> 'partner.dev'
  const file = path.split('/').pop()?.replace(/\.md$/, '')
  if (!file) continue

  let key = file
  let variant: HelpVariant = 'user'
  if (file.endsWith('.dev')) {
    variant = 'dev'
    key = file.slice(0, -'.dev'.length)
  } else if (file.endsWith('.user')) {
    variant = 'user'
    key = file.slice(0, -'.user'.length)
  }

  ;(helpByKey[key] ??= {})[variant] = modules[path]
}

/** Contenu Markdown pour une clé + variante, ou `null`. */
export function getHelp(
  key: string | null | undefined,
  variant: HelpVariant,
): string | null {
  if (!key) return null
  return helpByKey[key]?.[variant] ?? null
}

/** Indique si une variante précise existe pour la clé. */
export function hasVariant(
  key: string | null | undefined,
  variant: HelpVariant,
): boolean {
  return !!key && !!helpByKey[key]?.[variant]
}

/** Indique si au moins une variante existe pour la clé. */
export function hasAnyHelp(key: string | null | undefined): boolean {
  if (!key) return false
  const entry = helpByKey[key]
  return !!entry && (!!entry.user || !!entry.dev)
}
